import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.customers.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.customers.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/create',
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await apiService.customers.create(customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.customers.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.customers.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  customers: [],
  currentCustomer: null,
  loading: false,
  error: null,
  total: 0,
  pagination: {
    page: 1,
    limit: 100,
    total: 0,
    pages: 0,
  },
  success: null,
  timestamp: null,
  filters: {
    search: '',
    status: 'all',
  },
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentCustomer: (state) => {
      state.currentCustomer = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all customers
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};

        // Response shape: { success, message, data, pagination, timestamp }
        state.success = payload.success ?? true;
        state.timestamp = payload.timestamp || null;

        state.customers = payload.data || payload || [];

        const pag = payload.pagination || {};
        const total = pag.total ?? payload.total ?? (Array.isArray(state.customers) ? state.customers.length : 0);
        const limit = pag.limit ?? state.pagination.limit ?? 100;
        const page = pag.page ?? state.pagination.page ?? 1;
        const pages = (pag.pages ?? Math.ceil(total / limit)) || 0;

        state.total = total;
        state.pagination = { page, limit, total, pages };
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch customer by ID
      .addCase(fetchCustomerById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCustomer = action.payload;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create customer
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.customers.unshift(action.payload);
        state.total += 1;
        state.pagination.total = (state.pagination.total || 0) + 1;
        state.pagination.pages = Math.ceil(state.pagination.total / state.pagination.limit) || state.pagination.pages;
      })
      // Update customer
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const index = state.customers.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        if (state.currentCustomer?._id === action.payload._id) {
          state.currentCustomer = action.payload;
        }
      })
      // Delete customer
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.customers = state.customers.filter(c => c._id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        state.pagination.total = Math.max(0, (state.pagination.total || 0) - 1);
        state.pagination.pages = Math.ceil(state.pagination.total / state.pagination.limit) || 0;
      });
  },
});

export const { setFilters, clearCurrentCustomer, clearError } = customersSlice.actions;
export default customersSlice.reducer;

